import { useState, useEffect } from 'react';
import './UserProfile.css';
import { getCurrentUser } from '../../services/auth.service';
import {
  refreshToken, getUserCompany, getSeniorityLevels, getCountries, getDepartments,
  getPublicUserProfile,
  getPublicUserProfileById,
  createBuyerDemo, addSellerRecommendationStatus, addDemoMessages,
  checkSubscriptionStatus
} from '../../utils/api';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import { Helmet } from "react-helmet";
import { isAuthed, isBuyer, isSeller } from '../../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { Link, useParams } from 'react-router-dom';
import iconCompanyDefault from '../../assets/companylogos/default.png';
import JoinModalSeller from '../signup/JoinModalSeller';
import { FaLinkedin } from 'react-icons/fa';
import close from '../../assets/design/dashboard/close.png';
import useSubscriberStatus from '../../hooks/useSubscriberStatus';
import { ArrowLeft, Building, MapPin, Calendar, Star, Users, Briefcase, CreditCard, Video, MessageSquare, Mail, Phone, Globe, Award, CheckCircle, TrendingUp, DollarSign, User, X } from "lucide-react";


const UpgradeDialog = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Upgrade Required</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">
          Upgrade your account to invite potential buyers to product demos. This feature is available only for premium subscribers.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // TODO: Add upgrade flow navigation
              navigate('/subscription');
              onClose();
            }}
            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Add these helper functions before the UserProfile component
const formatTime = (time) => {
  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  return new Date(0, 0, 0, hours, minutes).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const getDayName = (dayNumber) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayNumber];
};


const UserProfile = ({ user_slug, user_id }) => {
  let user = getCurrentUser();
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [userCompany, setUserCompany] = useState(null);
  const [seniority, setSeniority] = useState([]);
  const [jobTitle, setJobTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [biography, setBiography] = useState('');
  const [showBooking, setShowBooking] = useState(false);
  const [showBookingConfirm, setShowBookingConfirm] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loadedSubscriptionCheck, setLoadedSubscriptionCheck] = useState(false);
  const imageUrl = process.env.REACT_APP_IMAGE_URL;
  const baseImageUrl = process.env.REACT_APP_BASE_IMAGE_URL;
  const navigate = useNavigate();
  let { slug } = useParams();

  useEffect(() => {
    if (isAuthed())
      setIsLoggedIn(true);

        async function loadSubscriptionCheck() {
          let res = await checkSubscriptionStatus();
    
          if (res.success) {
            setHasSubscription(res.data);
            setLoadedSubscriptionCheck(true);
          }
        }

    async function loadUserProfile() {
      if (user_id && user_id !== '') {
        let res = await getPublicUserProfileById(user_id);

        if (res.success) {
          setBuyer(res.data);
          setUserProfile(res.data);
          setJobTitle(res.data.job_title);
          setFirstName(res.data.first_name);
          setLastName(res.data.last_name);
          setBiography(res.data.biography);
          setLoaded(true);
        }
      }

      else {
        if (user_slug && user_slug !== '') {
          slug = user_slug;
        }

        let res = await getPublicUserProfile(slug);

        if (res.success) {
          setBuyer(res.data);
          setUserProfile(res.data);
          setJobTitle(res.data.job_title);
          setFirstName(res.data.first_name);
          setLastName(res.data.last_name);
          setBiography(res.data.biography);
          setLoaded(true);
        }
      }
    }

    async function loadUserCompany() {
      let res = await getUserCompany();

      if (res.success) {
        setUserCompany(res.data);
        setLoaded(true);
      }
    }

    async function loadSeniority() {
      let data = await getSeniorityLevels();
      setSeniority(data.data);
    }

    loadSubscriptionCheck();
    loadSeniority();
    loadUserProfile();
    loadUserCompany();
  }, [slug]);

  async function handleBookDemo() {
    if (isLoggedIn)
      setShowBooking(true);
    else
      setShowJoin(true);
  }

  async function handleSendRequest() {
    setSubmitting(true);
    let data = { buyer_id: userProfile.registered_user_id, credit_cost: 5, message: 'Seller Booked Through Buyer Profile' }
    let res = await createBuyerDemo(data);

    if (res.success) {
      if (res.exists === true) {
        alert(res.message)
      }

      else {
        setShowBooking(false);
        setShowBookingConfirm(true);
      }

      setSubmitting(false);
    }

    else if (res.status === 403) {
      let _refresh = await refreshToken();

      if (_refresh.success) {
        localStorage.setItem('access_token', _refresh.data.accessToken);
        localStorage.setItem('refresh_token', _refresh.data.refreshToken);

        let res = await createBuyerDemo(data, 'Seller Booked Through Buyer Profile');

        if (res.success) {
          if (res.exists === true) {
            alert(res.message)
          }

          else {
            setShowBooking(false);
            setShowBookingConfirm(true);
          }
        }
      }

      setSubmitting(false);
    }
  }

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDemoRequest = () => {
    if (!isLoggedIn) {
      setShowLoginPrompt(true);
    } else {

      if (!hasSubscription) {
      setIsUpgradeDialogOpen(true);
      return;
    }
    
      setShowDemoDialog(true);
    }
  };

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    //toast.success("Demo request sent successfully!");
    setShowDemoDialog(false);
  };

  async function handleSubmit() {
    try {
      setIsSubmitting(true);
      let res = await addSellerRecommendationStatus(buyer.registered_user_id, 'Approved', 'Buyer Directory');

      if (res.success) {
        let data2 = { buyer_id: buyer.registered_user_id, credit_cost: 5, message: 'Buyer Profile' }
        let res2 = await createBuyerDemo(data2);
        let refid = res2.data;

        if (message.length > 0)
          await addDemoMessages(refid, message);

        setSubmitted(true);

        if (res2.success) {
          setShowDemoDialog(false);
          setShowBookingConfirm(true);
          setMessage('');
        }
      }

    } catch (error) {
      console.error('Failed to send intro:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Helmet>
        <meta name='description' content='' />
        <title>{"Book a demo with " + userProfile?.first_name + " " + userProfile?.last_name}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50">

        {/* Share Profile Banner */}
        {user && buyer && user.registered_user_id === buyer.registered_user_id && (
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <Users className="h-5 w-5" />
                </div>
                <div className="text-sm">
                  <p className="font-medium">This is your public profile</p>
                  <p>Share it with sellers to receive relevant demo requests</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Link
                  to="/account/profile"
                  className="flex items-center px-3 py-1.5 text-sm font-medium bg-white/20 rounded-lg hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Profile
                </Link>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    handleCopy();
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                >
                  {copied ? 'Copied!' : 'Copy Profile Link'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loaded == true && userProfile && (
          <div className="mx-auto px-4 py-2 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Profile Section */}
              <div className="lg:col-span-2 space-y-6">
                {/* Basic Info Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row gap-6">
                      <div className="flex-shrink-0">
                        <div className="h-24 w-24 rounded-full border-4 border-white shadow overflow-hidden flex items-center justify-center bg-purple-100 text-purple-700">
                          {userProfile.profile_image && userProfile.profile_image !== '' && (
                            <img src={baseImageUrl + 'profileimages/' + userProfile.profile_image} alt="logo found" className='h-full w-full object-cover' />
                          )}

                          {(!userProfile.profile_image || userProfile.profile_image === '') && (
                            <img src={iconCompanyDefault} className='h-full w-full object-cover' alt="logo not found" />
                          )}
                        </div>
                      </div>

                      <div className="flex-1 text-center sm:text-left">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                          <div className='p-2'>
                            <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                              {buyer.first_name} {buyer.last_name}
                              {buyer.linkedin_url && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                  <FaLinkedin className="h-3 w-3 mr-1" />
                                  Verified
                                </span>
                              )}
                              {buyer.is_exclusive && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-200 to-yellow-400 text-amber-900 border border-amber-300 shadow-sm animate-pulse-subtle">
                                  <Star className="h-3.5 w-3.5 mr-1 fill-amber-600 stroke-amber-600" />
                                  Exclusive Member
                                </span>
                              )}
                            </h2>
                            <p className="text-lg text-gray-600 mb-2">{buyer.job_title}</p>
                            <div className="flex items-center justify-center sm:justify-start text-gray-500 mb-2">
                              <Building className="h-4 w-4 mr-2" />
                              <span>{buyer.company_name} • {buyer.company_size}</span>
                            </div>
                            <div className="flex items-center justify-center sm:justify-start text-gray-500">
                              <MapPin className="h-4 w-4 mr-2" />
                              <span>{buyer.country_name}</span>
                            </div>
                          </div>

                          {/* Removed demo request button from here */}
                        </div>

                        <p className="text-gray-700 leading-relaxed">{buyer.company_description}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Professional Biography - Only show if exists */}
                {buyer.biography && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="h-5 w-5 mr-2" />
                      Professional Biography
                    </h3>

                    <div className="space-y-4">
                      {buyer.biography}
                    </div>
                  </div>
                )}

                {buyer.company_description && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Building className="h-5 w-5 mr-2" />
                    Company Details
                  </h3>

                  <div className="space-y-4">
                    {buyer.company_description}
                  </div>
                </div>
                )}

                {/* Professional Details */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2" />
                    Professional Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Seniority Level
                        </label>
                        <p className="text-gray-900">{buyer.seniority}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Department</label>
                        <p className="text-gray-900">{buyer.department_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Team Size</label>
                        <p className="text-gray-900">{buyer.company_size}</p>
                      </div>

                    </div>

                    {/* <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Annual Budget</label>
                    <p className="text-gray-900">{buyer.annualBudget}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Purchase Range</label>
                    <p className="text-gray-900">{buyer.budgetRange}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Previous Purchases</label>
                    <p className="text-gray-900">{buyer.previousPurchases} software purchases</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Education</label>
                    <p className="text-gray-900">{buyer.education}</p>
                  </div>
                </div> */}
                  </div>
                </div>

                {/* Technology & Goals Section */}
                {(buyer.technology_stack || buyer.technology_goals) && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Globe className="h-5 w-5 mr-2" />
                      Technology & Goals
                    </h3>

                    <div className="space-y-6">
                      {buyer.technology_stack && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 block mb-2">Current Tech Stack</label>
                          <div className="flex flex-wrap gap-2">
                            {buyer.technology_stack}
                          </div>
                        </div>
                      )}

                      {buyer.technology_goals && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 block mb-2">Technology Goals</label>
                          <p className="text-gray-700">{buyer.technology_goals}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Business Context Section */}
                {(buyer.business_challenges || buyer.purchasing_role) && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2" />
                      Business Context
                    </h3>

                    <div className="space-y-6">
                      {buyer.business_challenges && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 block mb-2">Current Challenges</label>
                          <div className="space-y-3">
                            {buyer.business_challenges}
                          </div>
                        </div>
                      )}

                      {buyer.purchasing_role && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 block mb-2">Role in Purchasing</label>
                          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {buyer.purchasing_role}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Current Challenges */}
                {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Current Challenges & Priorities
              </h3>
              
              <div className="space-y-3">
                {buyer.currentChallenges.map((challenge, index) => (
                  <div key={index} className="flex items-start">
                    <div className="h-2 w-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <p className="text-gray-700">{challenge}</p>
                  </div>
                ))}
              </div>
            </div> */}

                {/* Technologies & Skills */}
                {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Technologies & Skills</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-2">Current Tech Stack</label>
                  <div className="flex flex-wrap gap-2">
                    {buyer.technologies.map((tech, index) => (
                      <span key={index} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-2">Areas of Interest</label>
                  <div className="flex flex-wrap gap-2">
                    {buyer.interests.map((interest, index) => (
                      <span key={index} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-2">Certifications</label>
                  <div className="flex flex-wrap gap-2">
                    {buyer.certifications.map((cert, index) => (
                      <span key={index} className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                        <Award className="h-3 w-3 mr-1" />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div> */}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Contact Info */}
                {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              
              <div className="space-y-3">
                {buyer.email && (
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-gray-400 mr-3" />
                    <span className="text-sm text-gray-600">Email available</span>
                  </div>
                )}

                <div className="flex items-center">
                  <Globe className="h-4 w-4 text-gray-400 mr-3" />
                  <span className="text-sm text-gray-600">{buyer.website}</span>
                </div>
              </div>
            
            </div> */}

                {/* How to Request Demo */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Video className="h-5 w-5 mr-2" />
                    How to Request a Demo
                  </h3>

                  <button
                    onClick={handleDemoRequest}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center transition-colors mb-6"
                  >
                    <Video className="h-5 w-5 mr-2" />
                    Request Demo
                  </button>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-purple-600">1</span>
                      </div>
                      <p className="text-sm text-gray-600">Click the "Request Demo" button above</p>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-purple-600">2</span>
                      </div>
                      <p className="text-sm text-gray-600">Write a brief message explaining your solution</p>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-purple-600">3</span>
                      </div>
                      <p className="text-sm text-gray-600">Submit your request and wait for a response</p>
                    </div>

                    <div className="pt-3 mt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">Demo requests are typically reviewed within 24-48 hours. You'll be notified via email when {buyer?.first_name} responds.</p>
                    </div>
                  </div>
                </div>

                {/* Work Hours */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Calendar className="h-5 w-5 mr-2" />
                      Work Hours
                    </h3>
                    {user && buyer && user.registered_user_id === buyer.registered_user_id && (
                      <Link
                        to="/account/workhours"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Hours
                      </Link>
                    )}
                  </div>

                  <div className="space-y-3">
                    {buyer.work_hours && buyer.work_hours.map((schedule) => (
                      <div key={schedule.work_hour_id} className="flex justify-between">
                        <span className="text-sm text-gray-600">{getDayName(schedule.day_of_week)}</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                        </span>
                      </div>
                    ))}
                    {(!buyer.work_hours || buyer.work_hours.length === 0) && (
                      <p className="text-sm text-gray-500">No work hours specified</p>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Timezone</span>
                      <span className="text-sm font-medium text-gray-900">{buyer.timezone || 'EST'}</span>
                    </div>
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">Response times may vary. Typically responds within 24-48 hours.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}



        {/* Demo Request Dialog */}
        {showDemoDialog && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative overflow-hidden">
              <button
                onClick={() => setShowDemoDialog(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Demo with {buyer.first_name} {buyer.last_name}</h3>
                <p className="text-sm text-gray-500 mb-6">{buyer.job_title} at {buyer.company_name}</p>



                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Message (Optional)</label>
                  <textarea
                    placeholder="Tell them why your solution would be valuable for their current challenges..."
                    className="w-full min-h-[100px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  ></textarea>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowDemoDialog(false)}
                    className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                    className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Video className="h-4 w-4 mr-2" />
                        Send Demo Request
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Login Prompt Dialog */}
        {showLoginPrompt && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative overflow-hidden">
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Join to Request Demos</h3>

                <div className="space-y-4">
                  <div className="bg-blue-50 text-blue-700 p-4 rounded-lg">
                    <p className="font-medium">Create a free account to:</p>
                    <ul className="mt-2 space-y-2">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>Request demos with buyers</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>Track your demo requests</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>Message buyers directly</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowLoginPrompt(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-300"
                    >
                      Cancel
                    </button>
                    <Link
                      to="/signup/seller"
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg"
                    >
                      Create Account
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showBooking && (
        <>
          <div className='overlay'></div>
          <div className='modal'>
            <div className='modal__close' onClick={() => setShowBooking(false)}>
              <img src={close} />
            </div>
            <h2 className='modal__h2'>Send a meeting request</h2>
            <div className='' style={{ marginTop: "30px" }}>
              <p>
                Interested in booking a meeting with me? Send a request and if I'm interested in your product/service I will apply right away!
              </p>
            </div>

            {submitting === false && (
              <div style={{ marginTop: "30px" }} className='center'>
                <button className='btn btn--primary' onClick={() => handleSendRequest()}>Send Request</button>
              </div>
            )}

            {submitting && (
              <Spinner />
            )}
          </div>
        </>
      )}

      {showBookingConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative overflow-hidden">
            <button
              onClick={() => setShowBookingConfirm(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-6">
              <div className="flex flex-col items-center text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>

                <h3 className="text-lg font-medium text-gray-900 mb-2">Request Sent Successfully!</h3>

                <p className="text-sm text-gray-500">
                  Your demo request has been sent. You will be notified when {buyer?.first_name} submits available times.
                </p>

                <button
                  onClick={() => setShowBookingConfirm(false)}
                  className="mt-6 inline-flex justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showJoin && (
        <>
          <FadeIn delay={250} duration={250}>
            <JoinModalSeller setShowModal={setShowJoin} />
          </FadeIn>
        </>
      )}

            <UpgradeDialog
        isOpen={isUpgradeDialogOpen}
        onClose={() => setIsUpgradeDialogOpen(false)}
      />
    </>
  )
}

export default UserProfile;